import JSZip from "jszip";
import dayjs from "global/dayjs";
import { TemplateKind } from "../../../templateConfig/validationRuleTypes";
import { UploadMode } from "../../FileUpload/uploadRules";
import { isJoinSkippedRow } from "../engine/validationTypes";
import { getValidationRun } from "../stores/validationResultStore";
import { buildLeftOutIssues } from "./leftOutIssues";
import { buildMainRowsWithoutMatchIssues } from "./mainRowsWithoutMatchIssues";
import { buildJoinLeftOutSheets } from "./leftOutSheets";
import { buildSheetWithoutRows } from "./worksheetRows";
import { AnnotateWorkerInput } from "../worker/workerProtocol";
import { runAnnotateInWorker } from "../worker/workerClient";

const bundleNamePrefix: Record<TemplateKind, string> = {
  StaticData: "staticDataImport",
  BalanceImport: "balanceImport",
};

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function buildAndDownload(input: AnnotateWorkerInput): Promise<void> {
  const files = await runAnnotateInWorker(input);

  // Sequential downloads work from a user-initiated click; a popup blocker may keep only the first.
  for (const file of files) {
    downloadBlob(new Blob([file.buffer], { type: file.mimeType }), file.fileName);
  }
}

export async function downloadAnnotatedCopy(
  submissionID: string,
  templateType: TemplateKind,
  mode: UploadMode,
  sourceFileName: string
): Promise<void> {
  const run = getValidationRun(submissionID);
  if (!run) throw new Error("No validation results to annotate");

  await buildAndDownload({
    templateType,
    mode,
    variant: "annotated",
    sourceFileName,
    sheets: [...run.rawSheets, ...buildJoinLeftOutSheets(run.skippedRowsByWorksheet)],
    issues: [
      ...run.issues.map(({ worksheet, rowNumber, field, message }) => ({ worksheet, rowNumber, field, message })),
      ...buildLeftOutIssues([...run.skippedRowsByWorksheet.values()].flat().filter((row) => !isJoinSkippedRow(row))),
      ...buildMainRowsWithoutMatchIssues(run.worksheetJoins, run.sheets),
    ],
  });
}

const safeReferenceToken = (reference: string) => reference.replace(/[^A-Za-z0-9._-]+/g, "-");

// Builds the import zip without the left-out rows; rows the join left out are never in the joined sheet
export async function buildCsvBundle(
  submissionID: string,
  templateType: TemplateKind,
  staticDataReference?: string
): Promise<{ blob: Blob; fileName: string }> {
  const run = getValidationRun(submissionID);
  if (!run) throw new Error("No validation run to export");

  const files = await runAnnotateInWorker({
    templateType,
    mode: UploadMode.Csv,
    variant: "plain",
    sourceFileName: "import",
    sheets: run.rawSheets.map((sheet) => {
      const skippedRows = run.skippedRowsByWorksheet.get(sheet.worksheet) ?? [];
      const fileRowNumbers = skippedRows.filter((row) => !isJoinSkippedRow(row)).map((row) => row.rowNumber);
      return buildSheetWithoutRows(sheet, new Set(fileRowNumbers));
    }),
    issues: [],
  });

  const zip = new JSZip();
  for (const file of files) {
    zip.file(file.fileName, file.buffer);
  }
  const referencePart = staticDataReference ? `-${safeReferenceToken(staticDataReference)}` : "";
  return {
    blob: await zip.generateAsync({ type: "blob" }),
    fileName: `${bundleNamePrefix[templateType]}${referencePart}-${dayjs().format("YYYY-MM-DD")}-output.zip`,
  };
}

export function downloadBundleBlob(blob: Blob, fileName: string): void {
  downloadBlob(blob, fileName);
}
